// 사람 신호 게이트 — GA·애드센스를 "사람이 실제로 조작한 뒤에만" 켜기 위한 단일 진입점(2026-10-02).
//
// 왜 필요한가: GA 실시간에 유럽 데이터센터 도시(프랑크푸르트·암스테르담·런던…)에서 활성 사용자
// 70여 명이 상시 잡혔다. first_visit 72 / scroll 72 / user_engagement 2 — 전원 신규, 소스 없음,
// 전원 페이지 끝까지 스크롤, 머문 기록 거의 없음. crawl:stats의 프록시 팜(주거용 프록시 + 진짜
// 모바일 UA)과 시간당 건수가 일치했다. 이 봇은 실브라우저라 JS를 실행하고 GA가 그대로 집계한다.
// 서버에서는 UA가 진짜 브라우저와 같아 막을 수 없으므로(functions/src/botPolicy.js 프록시 팜 주석)
// 클라이언트에서 "측정·광고를 켤지"만 가른다. 페이지 자체는 그대로 보여준다.
//
// ⚠️ 'scroll'은 사람 신호로 쓰지 않는다 — 위 봇이 정확히 스크롤을 한다(scroll 이벤트 = 사용자 수).
// 프로그램 스크롤(window.scrollTo)은 scroll만 만들고 touch/wheel은 만들지 않는다.
// isTrusted를 확인해 스크립트가 dispatchEvent로 만든 가짜 이벤트도 거른다.
//
// 대가: 진입만 하고 아무것도 안 건드린 채 나간 실사용자는 GA에 잡히지 않는다. 그런 방문은
// 원래 참여가 0이라 지표 왜곡은 오히려 줄어든다.

const HUMAN_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'mousemove'];

/**
 * 프록시 팜 UA인가 — 실제 브라우저가 만들 수 없는 형식(실제 기기명 + `Chrome/x.0.0.0`,
 * 또는 `CriOS/x.0.0.0`). functions/src/botPolicy.js isProxyFarmUa와 같은 규칙이다(functions는
 * packages/shared를 쓰지 않아 복제했다 — 한쪽을 고치면 다른 쪽도 고칠 것). 근거·검증은 그 파일 주석.
 */
export function isProxyFarmUa(ua = '') {
  const s = String(ua);
  if (/CriOS\/\d+\.0\.0\.0 /.test(s)) return true;
  const m = /\(Linux; Android [\d.]+; ([^;)]+)\) AppleWebKit\/537\.36 \(KHTML, like Gecko\) Chrome\/\d+\.0\.0\.0 Mobile Safari\/537\.36$/.exec(s);
  return !!m && m[1] !== 'K';
}

/**
 * 자동화·위조가 명백한 환경인가 (순수 함수 — 유닛 테스트용).
 * 위조 판정은 functions/src/botPolicy.js isForgedUa와 같은 규칙이다(복제 — 위와 같은 이유).
 */
export function isAutomatedClient({ ua = '', webdriver = false } = {}) {
  if (webdriver) return true;
  const s = String(ua);
  if (isProxyFarmUa(s)) return true;
  return /AppleWebKit\/60[0-9]\./i.test(s) && /Chrome\//i.test(s) && !/CriOS\//i.test(s);
}

let confirmed = false;
let started = false;
const waiters = new Set();

function confirm() {
  if (confirmed) return;
  confirmed = true;
  for (const ev of HUMAN_EVENTS) window.removeEventListener(ev, onInput, true);
  for (const cb of waiters) {
    try { cb(); } catch (e) { console.warn('[humanGate] 콜백 실패:', e?.message || e); }
  }
  waiters.clear();
}

function onInput(e) {
  if (e.isTrusted) confirm();
}

function start() {
  if (started || typeof window === 'undefined') return;
  started = true;
  const automated = isAutomatedClient({
    ua: navigator.userAgent,
    webdriver: navigator.webdriver === true,
  });
  if (automated) return;   // 영원히 확인되지 않는다 — GA·광고가 켜지지 않는다
  for (const ev of HUMAN_EVENTS) {
    window.addEventListener(ev, onInput, { capture: true, passive: true });
  }
}

/** 사람 신호가 확인되면 cb를 한 번 부른다. 이미 확인됐으면 즉시 부른다. 해제 함수를 돌려준다. */
export function onHumanConfirmed(cb) {
  start();
  if (confirmed) { cb(); return () => {}; }
  waiters.add(cb);
  return () => waiters.delete(cb);
}

export function isHumanConfirmed() {
  return confirmed;
}
